import { useState } from 'react';
import qs from 'qs';
import toast from 'react-hot-toast';
import { Loader2, Download } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemsApi } from '../../../hooks/useItemsApi';
import { getCategoryOptions, ScreenIdConst, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../../constants';
import { formatFromDateInput } from '../../../utils/date';
import { downloadCSV } from '../../../utils/csv';
import { generateBiddingCsvData } from '../../../components/admin/BiddingResult/biddingCsvMapper';
import { UserRole } from '../../../types/UserRole';
import { UserPermission } from '../../../types/UserPermission';

/**
 * 期間指定 落札結果CSV出力（市場専用）
 * 入札日(From/To)＋出品区分（県内/県外）を指定し、落札確定済みの結果をCSVで出力する。
 */
const MarketResultCsvPage = () => {

  const { currentUser } = useAuth();
  const { biddingGetAllResultListRange } = useItemsApi();

  //----------------
  // State
  //----------------
  const [fromDate, setFromDate] = useState<string>(''); // "yyyy-MM-dd"
  const [toDate, setToDate] = useState<string>('');     // "yyyy-MM-dd"
  const [combinedCategory, setCombinedCategory] = useState<string>(
    `${ListingCategoryConst.BIDDING}_${PrefTypeConst.INTERNAL}`,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageTitle = '落札結果CSV出力（期間指定）';

  //----------------
  // Variables
  //----------------
  const fromNum = fromDate ? Number(formatFromDateInput(fromDate)) : 0;
  const toNum = toDate ? Number(formatFromDateInput(toDate)) : 0;
  const isRangeInvalid = !!fromDate && !!toDate && fromNum > toNum;

  const isDisabled = isSubmitting || !fromDate || !toDate || isRangeInvalid;

  //----------------
  // Event Handlers
  //----------------
  const handleExport = async () => {
    if (isDisabled) return;

    setIsSubmitting(true);
    try {
      const { listingCategory, prefCategory } = parseCombinedCategory(combinedCategory);

      const queryString = qs.stringify({
        biddingDateFrom: fromNum,
        biddingDateTo: toNum,
        listingCategory,
        prefectureType: prefCategory,
      });

      const response = await biddingGetAllResultListRange(queryString);

      if (!response.isSuccess) {
        toast.error(response.message || '出力に失敗しました。');
        return;
      }

      const items = response.items || [];
      if (items.length === 0) {
        toast('対象データがありません');
        return;
      }

      if (!currentUser?.role) {
        toast.error('ユーザー権限が取得できませんでした。');
        return;
      }

      // 1. Mapperを使ってAPIデータをCSV用の二次元配列に変換
      const csvData = generateBiddingCsvData(items, currentUser.role as UserRole, currentUser.permission as UserPermission);

      // 1-1. 出力順の指定（鈴木要件 2026-07-15）: 入札日 > 入札No. > 入札日履歴行番号 の昇順。
      //      共有マッパー（既存の落札結果確認画面と共用）は変更せず、生成後のデータ行を本画面でのみ並べ替える。
      //      列位置はヘッダ行から実行時に解決（列構成の変更に追従）。数値化できるセルは数値として比較する。
      const [header, ...rows] = csvData;
      const dateIdx = header.indexOf('入札日');
      const noIdx = header.indexOf('入札No.');
      const historyIdx = header.indexOf('入札日履歴行番号');
      const toSortNum = (v: string | undefined): number => {
        const digits = String(v ?? '').replace(/\D/g, '');
        return digits === '' ? -1 : Number(digits);
      };
      if (dateIdx >= 0 && noIdx >= 0 && historyIdx >= 0) {
        rows.sort(
          (a, b) =>
            toSortNum(a[dateIdx]) - toSortNum(b[dateIdx]) ||
            toSortNum(a[noIdx]) - toSortNum(b[noIdx]) ||
            toSortNum(a[historyIdx]) - toSortNum(b[historyIdx]),
        );
      }
      const sortedCsvData = [header, ...rows];

      // 2. ファイル名を生成（例: 落札結果_20260710-20260712.csv）
      const fileName = `落札結果_${formatFromDateInput(fromDate)}-${formatFromDateInput(toDate)}.csv`;

      // 3. 汎用関数に渡してダウンロード実行
      downloadCSV(sortedCsvData, fileName);

      toast.success(`${items.length}件を出力しました`);
    } catch (err) {
      console.error('CSV出力中にエラーが発生しました', err);
      toast.error('CSVの作成に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  //----------------
  // Render
  //----------------
  return (
    <div className="bg-white min-h-screen font-sans pb-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ヘッダーセクション */}
        <header className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">{pageTitle}</h1>
        </header>

        <main>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex flex-wrap items-end gap-6 w-full">

              {/* 入札日From */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-bold text-slate-700 mb-1">入札日（開始）</label>
                <input
                  type="date"
                  name="biddingDateFrom"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full sm:w-48 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 appearance-none min-w-0 safari-date-fix"
                />
              </div>

              {/* 入札日To */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-bold text-slate-700 mb-1">入札日（終了）</label>
                <input
                  type="date"
                  name="biddingDateTo"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full sm:w-48 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 appearance-none min-w-0 safari-date-fix"
                />
              </div>

              {/* 出品区分セクション */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-bold text-slate-700 mb-1">出品区分</label>
                <select
                  className="w-full sm:w-56 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500"
                  value={combinedCategory}
                  onChange={(e) => setCombinedCategory(e.target.value)}
                >
                  {getCategoryOptions(ScreenIdConst.RESULT_CSV_RANGE, currentUser?.role).map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* CSV出力ボタン */}
              <div className="flex gap-1 ml-auto self-end">
                <button
                  disabled={isDisabled}
                  onClick={handleExport}
                  className={`w-56 py-2 text-white rounded shadow-sm font-bold transition-all flex items-center justify-center gap-2
                    ${isDisabled
                      ? 'bg-slate-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95 cursor-pointer'
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      出力中...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      CSV出力
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* メッセージ */}
            <div className="mt-3 flex flex-col gap-1">
              {isRangeInvalid && (
                <span className="text-m text-red-500 font-medium whitespace-nowrap">
                  ※開始日は終了日以前を指定してください
                </span>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MarketResultCsvPage;
